import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { 
  unifiedColors,
  portalThemes, 
  semanticColors, 
  neutralColors, 
  backgrounds, 
  textColors,
  getCurrentPortalTheme 
} from './colors';
import { typography } from './typography';
import { spacing } from './spacing';
import { shadows } from './shadows';
import { components } from './components';

/**
 * Production-ready Theme Provider for Bare Biologics
 * 
 * Features:
 * - Automatic portal detection
 * - Theme switching capability
 * - Centralized design tokens
 * - Type-safe theme access
 */

// Theme Context
const BareThemeContext = createContext({
  currentPortal: 'client',
  setPortal: () => {},
  theme: null,
  colors: null
});

// Custom hook to use theme
export const useBareTheme = () => {
  const context = useContext(BareThemeContext);
  if (!context) {
    throw new Error('useBareTheme must be used within BareThemeProvider');
  }
  return context;
};

// Create MUI theme using UNIFIED colors for ALL portals
const createPortalTheme = (portal) => {
  // Create a theme object structure that components function expects
  const unifiedTheme = {
    primary: unifiedColors.primary.main,
    primaryLight: unifiedColors.primary.light,
    primaryDark: unifiedColors.primary.dark,
    primaryContrast: unifiedColors.primary.contrast,
    accent: unifiedColors.secondary.main,
    hover: unifiedColors.button.outlineHover,
    active: 'rgba(13, 148, 136, 0.2)',
    navbar: unifiedColors.navigation,
    sidebar: {
      background: '#ffffff',
      activeItem: 'rgba(13, 148, 136, 0.1)',
      hoverItem: 'rgba(13, 148, 136, 0.05)',
      text: '#374151',
      activeText: unifiedColors.primary.main
    }
  };
  
  return createTheme({
    // Color Palette
    palette: {
      mode: 'light',
      
      primary: {
        main: unifiedColors.primary.main,
        light: unifiedColors.primary.light,
        dark: unifiedColors.primary.dark,
        contrastText: unifiedColors.primary.contrast,
      },
      
      secondary: {
        main: unifiedColors.secondary.main,
        light: unifiedColors.secondary.light,
        dark: unifiedColors.secondary.dark,
        contrastText: unifiedColors.secondary.contrast,
      },
      
      success: {
        main: semanticColors.success.main,
        light: semanticColors.success.light,
        dark: semanticColors.success.dark,
        contrastText: semanticColors.success.contrastText,
      },
      warning: {
        main: semanticColors.warning.main,
        light: semanticColors.warning.light,
        dark: semanticColors.warning.dark,
        contrastText: semanticColors.warning.contrastText,
      },
      error: {
        main: semanticColors.error.main,
        light: semanticColors.error.light,
        dark: semanticColors.error.dark,
        contrastText: semanticColors.error.contrastText,
      },
      info: {
        main: semanticColors.info.main,
        light: semanticColors.info.light,
        dark: semanticColors.info.dark,
        contrastText: semanticColors.info.contrastText,
      },
      
      background: {
        default: backgrounds.default,
        paper: backgrounds.paper,
      },
      
      text: {
        primary: textColors.primary,
        secondary: textColors.secondary,
        disabled: textColors.disabled,
      },
      
      grey: neutralColors.gray,
      
      // Default color for components that use color="default"
      default: {
        main: neutralColors.gray[300],
        light: neutralColors.gray[200],
        dark: neutralColors.gray[400],
        contrastText: neutralColors.gray[900],
      },
      
      divider: neutralColors.gray[200],
      
      // Custom colors (accessible via theme.palette)
      custom: {
        unified: unifiedColors,
        neutral: neutralColors,
        backgrounds: backgrounds,
      }
    },
    
    // Typography
    typography: typography,
    
    // Spacing
    spacing: spacing.unit,
    
    // Shadows
    shadows: shadows,
    
    // Shape
    shape: {
      borderRadius: 0,
    },
    
    // Component overrides (using unified theme)
    components: components(unifiedTheme),
    
    // Breakpoints
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    
    // Z-index
    zIndex: {
      appBar: 1200,
      drawer: 1100,
      modal: 1300,
      snackbar: 1400,
      tooltip: 1500,
    },
    
    // Transitions
    transitions: {
      easing: {
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
        sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
      },
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },
  });
};

// Main Theme Provider Component
export const BareThemeProvider = ({ children, initialPortal = null }) => {
  // Detect current portal from URL
  const detectPortal = () => {
    const path = window.location.pathname;
    if (path.includes('/internal')) return 'internal';
    if (path.includes('/rep')) return 'rep';
    return 'client';
  };
  
  const [currentPortal, setCurrentPortal] = useState(
    initialPortal || detectPortal()
  );
  
  // Listen for route changes
  useEffect(() => {
    const checkPortal = () => {
      const detectedPortal = detectPortal();
      if (detectedPortal !== currentPortal) {
        setCurrentPortal(detectedPortal);
      }
    };
    
    // Check on route change
    window.addEventListener('popstate', checkPortal);
    
    // Check periodically (for client-side routing)
    const interval = setInterval(checkPortal, 500);
    
    return () => {
      window.removeEventListener('popstate', checkPortal);
      clearInterval(interval);
    };
  }, [currentPortal]);
  
  // Create theme based on current portal
  const theme = useMemo(
    () => createPortalTheme(currentPortal),
    [currentPortal]
  );
  
  // Use UNIFIED colors for all portals
  const colors = useMemo(
    () => ({
      primary: unifiedColors.primary.main,
      primaryLight: unifiedColors.primary.light,
      primaryDark: unifiedColors.primary.dark,
      primaryContrast: unifiedColors.primary.contrast,
      secondary: unifiedColors.secondary.main,
      secondaryLight: unifiedColors.secondary.light,
      secondaryDark: unifiedColors.secondary.dark,
      secondaryContrast: unifiedColors.secondary.contrast,
      ...unifiedColors
    }),
    [] // No dependencies since we use unified colors
  );
  
  // Context value
  const contextValue = useMemo(
    () => ({
      currentPortal,
      setPortal: setCurrentPortal,
      theme,
      colors,
    }),
    [currentPortal, theme, colors]
  );
  
  return (
    <BareThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </BareThemeContext.Provider>
  );
};

// Export for use in non-React contexts
export const getThemeForPortal = (portal) => createPortalTheme(portal);

// Helper HOC for portal-specific components
export const withPortalTheme = (Component, portal) => {
  return (props) => {
    const theme = useMemo(() => createPortalTheme(portal), []);
    return (
      <MuiThemeProvider theme={theme}>
        <Component {...props} />
      </MuiThemeProvider>
    );
  };
};
